import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import { useSnackbar } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { JoystickActionConfig, JoystickConfigMap, type JoystickInput } from '@/providers';
import {
  isJoystickInputPhysicallyReleased,
  norm,
  releaseAll,
  resetAnalogTriggerLatch,
  sampleActiveInputs,
} from '@/providers/gamepadProvider/useGamepad/useGamepad.help.ts';

type UseJoystickProps = {
  inputConfig: JoystickConfigMap;
  /** Axis movement threshold for analog inputs (0-1); below this is considered "not moved" */
  axisThreshold?: number;
  setIsGamepadConnected: Dispatch<SetStateAction<boolean>>;
  setGamepadId: Dispatch<SetStateAction<string>>;
};

/** ---- internals (module singletons per hook instance) ---- */
const held = new Set<string>(); // currently held inputs
/** Last handler fire time per input — used for debouncing while repeating */
const lastFireTimes = new Map<string, number>();

/**
 * After a `repeat: false` handler fires, the input id stays here until the hardware reports a full
 * release. Prevents re-firing when `held` drops for one frame or analog values glitch while still
 * physically held/released ambiguously.
 */
const nonRepeatConsumedUntilPhysicalRelease = new Set<string>();

/** Core hook */
export const useGamepadControl = ({
  inputConfig,
  axisThreshold = 0.1,
  setIsGamepadConnected,
  setGamepadId,
}: UseJoystickProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['cameraControl']);

  // normalize keys (B0 -> b0, etc)
  // eslint-disable-next-line react-x/no-unnecessary-use-memo
  const inputConfigMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(inputConfig).map(([k, cfg]) => [norm(k), cfg])
      ) as JoystickConfigMap,
    [inputConfig]
  );

  useEffect(() => {
    let rafId: number | null = null;

    /** Main polling loop with edge/level logic similar to keydown/up */
    const pollGamepad = () => {
      // Only the focused window should act on gamepad input. The Gamepad API is shared across all
      // windows/tabs of the same browser, so without this guard every open window would fire the
      // same actions simultaneously. Held state is released via the `blur` handler on focus loss.
      if (!document.hasFocus()) {
        rafId = requestAnimationFrame(pollGamepad);
        return;
      }

      const pads = navigator.getGamepads
        ? Array.from(navigator.getGamepads()).filter((p): p is Gamepad => Boolean(p))
        : [];

      if (pads.length) {
        const gp = pads[0]!;
        const activeNow = sampleActiveInputs(gp, inputConfigMap, axisThreshold);

        for (const id of nonRepeatConsumedUntilPhysicalRelease) {
          if (isJoystickInputPhysicallyReleased(gp, id, axisThreshold)) {
            nonRepeatConsumedUntilPhysicalRelease.delete(id);
          }
        }

        // newly pressed
        activeNow.forEach((values, id) => {
          const cfg: JoystickActionConfig | undefined = inputConfigMap[id as JoystickInput];
          if (!cfg) return;

          if (!cfg.repeat && nonRepeatConsumedUntilPhysicalRelease.has(id)) {
            held.add(id);
            return;
          }

          const now = performance.now();
          const configured = cfg.intervalMs ?? 100;
          const debounceMs = cfg.repeat ? configured : (cfg.intervalMs ?? 0);
          const last = lastFireTimes.get(id);

          const isNewPress = !held.has(id);
          if (!isNewPress && !cfg.repeat) {
            // already held and no repeat: do nothing here, interval handles repeat
            return;
          }

          if (last != null && now - last < debounceMs) {
            return;
          }

          held.add(id);
          lastFireTimes.set(id, now);
          if (!cfg.repeat) {
            nonRepeatConsumedUntilPhysicalRelease.add(id);
          }

          const handler = cfg.handler;
          if (!handler) {
            // should not happen due to cfg presence check
            enqueueSnackbar(t(`noHandlerDefinedForInput`, { id: id }), {
              variant: 'warning',
            });
            return;
          }

          handler(values);
        });

        // released: held but not in activeNow
        Array.from(held).forEach(id => {
          if (!activeNow.has(id)) {
            held.delete(id);
            const releasedCfg = inputConfigMap[id as JoystickInput];
            // Repeat timers reset on release; non-repeat retains lastFireTimes through chatter/release.
            if (releasedCfg?.repeat) {
              lastFireTimes.delete(id);
            }
            releasedCfg?.stopHandler?.();
          }
        });
      }

      rafId = requestAnimationFrame(pollGamepad);
    };

    const onConnect = (ev: GamepadEvent) => {
      setIsGamepadConnected(true);
      setGamepadId(ev.gamepad.id);

      enqueueSnackbar(t('gamepadConnected'), {
        variant: 'info',
        autoHideDuration: 3000,
        key: ev.gamepad.id,
      });
    };

    const onDisconnect = (ev: GamepadEvent) => {
      setIsGamepadConnected(false);
      setGamepadId('');
      resetAnalogTriggerLatch();
      releaseAll(held, inputConfigMap, nonRepeatConsumedUntilPhysicalRelease);
      enqueueSnackbar(t('gamepadDisconnected'), {
        variant: 'warning',
        autoHideDuration: 3000,
        key: ev.gamepad.id,
      });
    };

    const onBlur = () => {
      resetAnalogTriggerLatch();
      releaseAll(held, inputConfigMap, nonRepeatConsumedUntilPhysicalRelease);
    };

    rafId = requestAnimationFrame(pollGamepad);
    window.addEventListener('gamepadconnected', onConnect);
    window.addEventListener('gamepaddisconnected', onDisconnect);
    window.addEventListener('blur', onBlur);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('gamepadconnected', onConnect);
      window.removeEventListener('gamepaddisconnected', onDisconnect);
      window.removeEventListener('blur', onBlur);
      // ensure everything stops on unmount

      resetAnalogTriggerLatch();
      releaseAll(held, inputConfigMap, nonRepeatConsumedUntilPhysicalRelease);
    };
  }, [axisThreshold, enqueueSnackbar, t, inputConfigMap, setIsGamepadConnected, setGamepadId]);
};
